import contactsHelper from './contacts';
import { toColumnKey } from '@helpers/csv-helper';
import { baseLanguage, normalizeBirthDate } from '@helpers/contact-demographics';

export const validateBulkData = (data, locale) => {
    if (!data || data.length === 0) return null;
    const list = [];
    data.forEach(element => {
        element = convertToPluspointModel(element);
        contactsHelper.checkPhoneNumbers(element, locale, 'phoneNumber');
        contactsHelper.checkEmails(element, 'email');
        contactsHelper.checkDemographics(element);
        list.push(element);
    });
    const filteredEmptyValues = list.filter(item => item.phoneNumber || item.email);
    return checkUniqueContacts(filteredEmptyValues, 'phoneNumber');
};

export const CSV_HEADERS = {
    firstName: ['First Name'],
    lastName: ['Last Name'],
    phoneNumber: ['Mobile Number'],
    email: ['Email'],
    facebook: ['Facebook'],
    instagram: ['Instagram'],
    externalId: ['External ID', 'External Id'],
    sex: ['Sex'],
    dateOfBirth: ['Date of Birth'],
    language: ['Language'],
};

const readCell = (row, field) =>
    CSV_HEADERS[field].map(header => row[toColumnKey(header)]).find(value => value !== undefined);

export const convertToPluspointModel = row => {
    const cell = field => readCell(row, field)?.trim();
    return {
        phoneNumber: readCell(row, 'phoneNumber'),
        email: cell('email')?.toLowerCase(),
        firstName: readCell(row, 'firstName'),
        lastName: readCell(row, 'lastName'),
        instagram: readCell(row, 'instagram'),
        facebook: readCell(row, 'facebook'),
        phoneValid: false,
        emailValid: false,
        externalId: cell('externalId'),
        sex: cell('sex'),
        dateOfBirth: cell('dateOfBirth'),
        language: cell('language'),
    };
};

export const checkUniqueContacts = (list, field) => {
    const uniqueMap = {};
    const uniqueList = [];
    for (const contact of list) {
        if (!uniqueMap[contact[field]]) {
            if (contact[field]) uniqueMap[contact[field]] = true;
            uniqueList.push(contact);
            continue;
        }
    }
    return uniqueList;
};

const phoneDigits = value => `${value ?? ''}`.replace(/\D/g, '');

const equalsIgnoringCase = (a, b) => `${a ?? ''}`.toLowerCase() === `${b ?? ''}`.toLowerCase();

const IMPORT_FIELD_EQUALS = {
    externalId: (stored, imported) => !!stored || stored === imported,
    sex: equalsIgnoringCase,
    language: (stored, imported) => baseLanguage(stored) === baseLanguage(imported),
    dateOfBirth: (stored, imported) => {
        const [a, b] = [normalizeBirthDate(stored), normalizeBirthDate(imported)];
        return a === b || keepsKnownYear(a, b);
    },
};

const keepsKnownYear = (stored, imported) =>
    `${stored ?? ''}`.length === 10 && `${imported ?? ''}`.length === 5 && `${stored}`.endsWith(`-${imported}`);

export const importFieldEquals = (key, stored, imported) =>
    IMPORT_FIELD_EQUALS[key] ? IMPORT_FIELD_EQUALS[key](stored, imported) : stored === imported;

export const isDuplicate = (contact, duplicate) => {
    const contactPhone = phoneDigits(contact.phoneNumber);
    const duplicatePhone = phoneDigits(duplicate.phoneNumber);
    return (
        (contactPhone !== '' && contactPhone === duplicatePhone) ||
        (contactPhone === '' && duplicatePhone === '' && contact.email && duplicate.email === contact.email)
    );
};

export const findDuplicate = (contact, duplicates) => {
    const duplicatedContacts = duplicates?.filter(duplicate => isDuplicate(contact, duplicate));
    let duplicate = duplicatedContacts[0];
    if (duplicatedContacts.length > 1) {
        const emailMatch = duplicatedContacts
            .filter(duplicate => contact.email && duplicate.email === contact.email)
            .sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn))[0];
        if (emailMatch) {
            duplicate = emailMatch;
        }
    }
    return duplicate;
};

export const removeUnchangedContacts = (contacts, duplicates = []) => {
    const contactsForUpdate = [];
    contacts = contacts.filter(contact => {
        const duplicate = findDuplicate(contact, duplicates);
        let isChanged = false;
        if (duplicate) {
            isChanged =
                (contact.firstName && contact.firstName !== '-' && duplicate.firstName !== contact.firstName) ||
                (contact.lastName && contact.lastName !== '-' && duplicate.lastName !== contact.lastName) ||
                (contact.instagram && duplicate.instagram !== contact.instagram) ||
                (contact.facebook && duplicate.facebook !== contact.facebook) ||
                (contact.email && duplicate.email !== contact.email) ||
                (contact.externalId && !importFieldEquals('externalId', duplicate.externalId, contact.externalId)) ||
                (contact.sex && !importFieldEquals('sex', duplicate.sex, contact.sex)) ||
                (contact.dateOfBirth &&
                    !importFieldEquals('dateOfBirth', duplicate.dateOfBirth, contact.dateOfBirth)) ||
                (contact.language && !importFieldEquals('language', duplicate.language, contact.language));
        } else {
            isChanged = true;
        }
        if (isChanged && duplicate) {
            contactsForUpdate.push(duplicate);
        }
        return isChanged;
    });
    return { contacts, duplicates: contactsForUpdate };
};
